import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import { trackAIVisit, pageview } from './utils/analytics';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const ProjectPage = React.lazy(() => import('./pages/ProjectPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const RacePage = React.lazy(() => import('./pages/RacePage'));
const EquipmentsPage = React.lazy(() => import('./pages/EquipmentsPage'));
const Links = React.lazy(() => import('./pages/Links'));
const AppLandingPage = React.lazy(() => import('./pages/AppLandingPage'));
const Race2026Page = React.lazy(() => import('./pages/Race2026Page'));
const EquipmentGuidePage = React.lazy(() => import('./pages/EquipmentGuidePage'));
const PhotosPage = React.lazy(() => import('./pages/PhotosPage'));

const MEDIA_SELECTOR = 'img, video';

const hardenMediaElements = (root: ParentNode = document): void => {
  const mediaElements = root.querySelectorAll<HTMLElement>(MEDIA_SELECTOR);

  mediaElements.forEach((mediaElement) => {
    mediaElement.setAttribute('data-protected-media', 'true');
    mediaElement.setAttribute('draggable', 'false');
    mediaElement.classList.add('protected-media');

    if (mediaElement instanceof HTMLVideoElement) {
      mediaElement.setAttribute('controlsList', 'nodownload noremoteplayback');
      mediaElement.setAttribute('disablePictureInPicture', 'true');
    }
  });
};

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.isContentEditable) {
    return true;
  }

  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

const shouldProtectMedia = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest(`${MEDIA_SELECTOR}, [data-protected-media="true"]`));
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  useEffect(() => {
    // ページビュー送信
    pageview(location.pathname);
  }, [location]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="flex-grow pt-16">{children}</div>
      <Footer />
    </div>
  );
};

function App() {
  useEffect(() => {
    // アプリ起動時にAI経由チェック
    trackAIVisit();
  }, []);

  useEffect(() => {
    hardenMediaElements();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (node.matches(MEDIA_SELECTOR)) {
            hardenMediaElements(node.parentNode ?? document);
            return;
          }

          hardenMediaElements(node);
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const handleContextMenu = (event: MouseEvent) => {
      if (shouldProtectMedia(event.target)) {
        event.preventDefault();
      }
    };

    const handleDragStart = (event: DragEvent) => {
      if (shouldProtectMedia(event.target)) {
        event.preventDefault();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) {
        return;
      }

      const lowerKey = event.key.toLowerCase();
      const isSaveOrPrint = (event.metaKey || event.ctrlKey) && (lowerKey === 's' || lowerKey === 'p');
      const isViewSource = (event.metaKey || event.ctrlKey) && lowerKey === 'u';
      const isDevTools = event.key === 'F12';

      if (isSaveOrPrint || isViewSource || isDevTools) {
        event.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      observer.disconnect();
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Router>
      <Layout>
        <Suspense fallback={<div className="flex justify-center items-center min-h-[50vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div></div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/project" element={<ProjectPage />} />
            <Route path="/race" element={<RacePage />} />
            <Route path="/race/2026" element={<Race2026Page />} />
            <Route path="/equipments" element={<EquipmentsPage />} />
            <Route path="/photos" element={<PhotosPage />} />
            <Route path="/equipment-guide" element={<EquipmentGuidePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/links" element={<Links />} />
            <Route path="/app" element={<AppLandingPage />} />
          </Routes>
        </Suspense>
      </Layout>
    </Router>
  );
}

export default App;
